import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { AlertType, OptionType, StepState } from 'src/app/core/enums/enums';
import { ContractError } from 'src/app/core/models/models';
import { Step } from "src/app/core/models/step.model";
import {
  SpecialRequestCategory,
  SpecialRequestCategoryQuote,
  SpecialRequestConfirmation,
  SpecialRequestItem,
  SpecialRequestQuote,
  SpecialRequestQuoteRequest,
} from 'src/app/core/models/special-requests.model';

import { SpecialRequestActions } from '../actions';

export interface SpecialRequestState {
  loading: boolean;
  error: ContractError;
  specialRequestQuoteRequest: SpecialRequestQuoteRequest;
  specialRequestQuote: SpecialRequestQuote;
  specialRequestCategoryQuote: SpecialRequestCategoryQuote;
  specialRequestConfirmation: SpecialRequestConfirmation[];
  alertState: AlertType;
  steps: Step[];
  activeStep: number;
  selectedRequest: SpecialRequestItem;
  originalCategories: SpecialRequestCategory[];
  openAccordion: boolean;
}

export const initialState: SpecialRequestState = {
  loading: false,
  error: null,
  specialRequestQuoteRequest: null,
  specialRequestQuote: null,
  specialRequestCategoryQuote: null,
  specialRequestConfirmation: null,
  alertState: null,
  openAccordion: false,
  activeStep: 1,
  selectedRequest: null,
  originalCategories: [],
  steps: [
    {
      title: 'special-requests.step1',
      state: StepState.Active,
    },
    {
      title: 'special-requests.step2',
      state: StepState.Normal,
    },
  ],
};

const specialRequestReducer = createReducer(
  initialState,
  on(
    SpecialRequestActions.getSpecialRequestQuoteSuccess,
    (state, { specialRequestCategoryQuote }) => ({
      ...initialState,
      specialRequestCategoryQuote,
      originalCategories: specialRequestCategoryQuote.specialRequestCategories,
    })
  ),
  on(SpecialRequestActions.filteredSearch, (state, { filtered }) => ({
    ...state,
    specialRequestCategoryQuote: {
      ...state.specialRequestCategoryQuote,
      specialRequestCategories: filtered,
    },
    openAccordion: true,
  })),
  on(SpecialRequestActions.goToStep2, (state, { specialRequestItem }) => ({
    ...state,
    activeStep: 2,
    selectedRequest: specialRequestItem,
    steps: [
      {
        title: 'special-requests.step1',
        state: StepState.Completed,
      },
      {
        title: 'special-requests.step2',
        state: StepState.Active,
      },
    ],
  })),
  on(SpecialRequestActions.goToStep1, (state) => ({
    ...state,
    activeStep: 1,
    steps: [
      {
        title: 'special-requests.step1',
        state: StepState.Completed,
      },
      {
        title: 'special-requests.step2',
        state: StepState.Normal,
      },
    ],
  })),
  on(
    SpecialRequestActions.postSpecialRequest,
    (state, { specialRequestQuoteRequest }) => ({
      ...state,
      loading: true,
      specialRequestQuoteRequest,
    })
  ),
  on(
    SpecialRequestActions.postSpecialRequestSuccess,
    (state, { specialRequestConfirmation, isSuccessful }) => ({
      ...state,
      loading: false,
      specialRequestConfirmation,
      alertState: isSuccessful ? AlertType.Success : AlertType.Muted,
      steps: isSuccessful
        ? [
            {
              title: 'special-requests.step1',
              state: StepState.Completed,
            },
            {
              title: 'special-requests.step2',
              state: StepState.Completed,
            },
          ]
        : [
            {
              title: 'special-requests.step1',
              state: StepState.Completed,
            },
            {
              title: 'special-requests.step2',
              state: StepState.Muted,
            },
          ],
    })
  ),
  on(SpecialRequestActions.postSpecialRequestFail, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    alertState: AlertType.Error,
    steps: [
      {
        title: 'special-requests.step1',
        state: StepState.Completed,
      },
      {
        title: 'special-requests.step2',
        state: StepState.Error,
      },
    ],
  }))
);

export function reducer(
  state: SpecialRequestState | undefined,
  action: Action
): SpecialRequestState {
  return specialRequestReducer(state, action);
}

export const getSpecialRequestState = createFeatureSelector<
  SpecialRequestState
>(OptionType.SpecialRequest);

export const getLoading = createSelector(
  getSpecialRequestState,
  state => state.loading
);

export const getError = createSelector(
  getSpecialRequestState,
  state => state.error
);

export const getSpecialRequestQuote = createSelector(
  getSpecialRequestState,
  state => state.specialRequestQuote
);

export const getspecialRequestCategoryQuote = createSelector(
  getSpecialRequestState,
  state => state.specialRequestCategoryQuote
);

export const getSpecialRequestConfirmation = createSelector(
  getSpecialRequestState,
  state => state.specialRequestConfirmation
);

export const getStepLogic = createSelector(getSpecialRequestState, state => ({
  steps: state.steps,
  activeStep: state.activeStep,
}));

export const getRequestState = createSelector(
  getSpecialRequestState,
  state => state.alertState
);

export const getOpenAccordion = createSelector(
  getSpecialRequestState,
  state => state.openAccordion
);

export const getOriginalCategories = createSelector(
  getSpecialRequestState,
  state => state.originalCategories
);
